import {Component, Inject, OnInit} from '@angular/core';
import {UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {of} from 'rxjs';
import {catchError, finalize, take} from 'rxjs/operators';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {IntegrationId, ProjectId} from '@webmate-web-core/shared/types/ids';
import {ModalToolContainerService, WebmateToolConfig} from '../modaltoolcontainer/modaltoolcontainer.service';
import {TOOL_CONTAINER_DATA} from '../modaltoolcontainer/modaltoolcontainer.component';
import {
    IssueReporterAttachment,
    IssueReporterDefaults,
    IssueReporterIntegration,
    IssueReporterProjectWithIssueTypes,
    IssueSourceType
} from '@webmate-web-core/shared/types/integrations';
import {IssueReporterParameter, TestService} from '@webmate-web-core/shared/services/test.service';
import {WmNotificationService} from '@webmate-web-core/shared/components/notification/wm-notification.service';
import {IconDefinition} from '@fortawesome/fontawesome-svg-core';
import {faEnvelope, faExternalLinkAlt, faFile, faImage, faVideo} from '@fortawesome/pro-solid-svg-icons';
import {htmlToText} from 'html-to-text';
import moment from 'moment';
import {ImageService} from '@webmate-web-core/shared/services/image.service';

export class IssueReporterToolConfig implements WebmateToolConfig {
    toolName: string;

    constructor(
        public projectId: ProjectId,
        public issueSourceType: IssueSourceType,
        public issueSourceId: string,
        public issueReporterId?: IntegrationId
    ) {
        this.toolName = "Issue Reporter"
    }
}

interface DropdownOption<T> {
    label: string;
    value: T;
}

interface IssueFieldOption {
    label: string;
    value: any;
}

type AttachmentWithKey = IssueReporterAttachment & { key: string };

@UntilDestroy()
@Component({
    selector: 'wm-issue-reporter',
    templateUrl: './issue-reporter.component.html',
    styleUrls: ['./issue-reporter.component.less']
})
export class IssueReporterComponent implements OnInit {

    private static readonly JIRA_DATE_FORMAT = 'YYYY-MM-DD';

    form: UntypedFormGroup | undefined;

    issueReporterOptions: DropdownOption<IntegrationId>[] = [];
    selectedIssueReporterId: IntegrationId | undefined;
    issueReporters: IssueReporterIntegration[] = [];

    jiraProjects: IssueReporterProjectWithIssueTypes[] = [];
    jiraProjectOptions: DropdownOption<string>[] = [];
    selectedJiraProjectId: string | undefined;

    issueTypeOptions: DropdownOption<string>[] = [];
    selectedIssueTypeId: string | undefined;

    reporterFields: IssueReporterParameter[] = [];
    formFields: IssueReporterParameter[] = [];
    attachments: AttachmentWithKey[] = [];
    selectedAttachments: {[attachmentKey: string]: boolean} = {};
    supportsAttachments: boolean = false;

    loadingIssueReporters = false;
    loadingJiraProjects = false;
    loadingFields = false;
    loadingDefaults = false;
    submitting = false;
    submittedIssueUrl: string | undefined;

    private cachedDefaults: IssueReporterDefaults | undefined;

    constructor(
        private readonly formBuilder: UntypedFormBuilder,
        private readonly testService: TestService,
        @Inject(TOOL_CONTAINER_DATA) public data: IssueReporterToolConfig,
        private readonly toolService: ModalToolContainerService,
        private readonly notificationService: WmNotificationService,
        private readonly imageService: ImageService
    ) {}

    ngOnInit(): void {
        this.loadIssueReporters();
    }

    get contextLabel(): string {
        return this.data.issueSourceType === IssueSourceType.Finding ? 'Finding' : 'Test Run';
    }

    onReporterChange(issueReporterId: IntegrationId): void {
        this.selectedIssueReporterId = issueReporterId;
        this.cachedDefaults = undefined;
        this.jiraProjects = [];
        this.jiraProjectOptions = [];
        this.issueTypeOptions = [];
        this.selectedJiraProjectId = undefined;
        this.selectedIssueTypeId = undefined;
        this.resetForm();
        this.preloadDefaults();
        this.loadJiraProjects();
    }

    onJiraProjectChange(jiraProjectId: string): void {
        this.selectedJiraProjectId = jiraProjectId;
        this.issueTypeOptions = this.jiraProjects
            .find(project => project.id === jiraProjectId)
            ?.issueTypes.map(([id, name]) => ({label: name, value: id})) || [];
        this.selectedIssueTypeId = undefined;
        this.resetForm();
    }

    onIssueTypeChange(issueTypeId: string): void {
        this.selectedIssueTypeId = issueTypeId;
        this.resetForm();
        this.loadReporterFields();
    }

    resetForm(): void {
        this.form = undefined;
        this.reporterFields = [];
        this.formFields = [];
        this.attachments = [];
        this.selectedAttachments = {};
        this.supportsAttachments = false;
        this.submittedIssueUrl = undefined;
    }

    submit(): void {
        if (!this.form || !this.selectedIssueReporterId || !this.selectedJiraProjectId || !this.selectedIssueTypeId || this.form.invalid) {
            return;
        }

        const formFields = this.formFields;
        const params = Object.entries(this.form.getRawValue()).reduce((acc, [key, value]) => {
            const field = formFields.find(field => field.name === key);
            const fieldType = field?.parameterType;
            let data = value;

            if (field) {
                if (this.isDateField(field)) {
                    data = this.formatDateValue(value) ?? value;
                } else if (this.isDateTimeField(field)) {
                    data = this.formatDateTimeValue(value) ?? value;
                }
            }

            acc[key] = {data, type: fieldType};
            return acc;
        }, {} as {[key: string]: {data: any, type: string}});

        const projectField = this.findFieldByName('project');
        if (this.selectedJiraProjectId) {
            params['project'] = {data: this.selectedJiraProjectId, type: projectField?.parameterType || 'Enum'};
        }

        const issueTypeField = this.findFieldByName('issuetype');
        if (issueTypeField && this.selectedIssueTypeId) {
            params['issuetype'] = {data: this.selectedIssueTypeId, type: issueTypeField.parameterType || 'Enum'};
            if (issueTypeField.name !== 'issuetype' && params[issueTypeField.name]) {
                delete params[issueTypeField.name];
            }
        }

        const selectedAttachments = this.supportsAttachments
            ? this.attachments
                .filter(att => this.selectedAttachments[att.key])
                .map(att => att.observation)
            : [];

        this.submitting = true;
        this.testService.submitIssueReport(this.selectedIssueReporterId, params, selectedAttachments, this.data.projectId).pipe(
            finalize(() => this.submitting = false),
            untilDestroyed(this)
        ).subscribe({
            next: issueInfo => {
                this.notificationService.toastNotification('Issue submitted successfully.');
                const baseUri = this.getSelectedReporterBaseUri();
                const issueKey = issueInfo.id;

                if (issueKey && baseUri) {
                    this.submittedIssueUrl = `${baseUri.replace(/\/+$/, '')}/browse/${issueKey}`;
                }
            },
            error: err => {
                console.warn('Submitting issue failed', err);
                this.notificationService.toastErrorNotification('Submitting issue failed.');
            }
        });
    }

    openIssueInJira(): void {
        if (this.submittedIssueUrl) {
            window.open(this.submittedIssueUrl, '_blank', 'noopener');
        }
    }

    cancel(): void {
        this.toolService.closeLastTool();
    }

    loadIssueReporters(): void {
        this.loadingIssueReporters = true;
        this.testService.getIssueReportersForCustomer().pipe(
            take(1),
            catchError(err => {
                console.warn('Failed to load issue reporters', err);
                return of([]);
            }),
            finalize(() => this.loadingIssueReporters = false),
            untilDestroyed(this)
        ).subscribe(reporters => {
            this.issueReporters = reporters || [];
            this.issueReporterOptions = this.issueReporters.map(reporter => ({
                label: reporter.name,
                value: reporter.id
            }));

            this.applyInitialReporterSelection();
        });
    }

    applyInitialReporterSelection(): void {
        const targetReporterId = this.data.issueReporterId && this.issueReporterOptions.some(opt => opt.value === this.data.issueReporterId)
            ? this.data.issueReporterId
            : this.issueReporterOptions[0]?.value;

        if (targetReporterId) {
            this.selectedIssueReporterId = targetReporterId;
            this.preloadDefaults();
            this.loadJiraProjects();
        }
    }

    loadJiraProjects(): void {
        if (!this.selectedIssueReporterId) {
            return;
        }

        this.loadingJiraProjects = true;
        this.jiraProjectOptions = [];
        this.issueTypeOptions = [];
        this.testService.getIssueReporterProjectsWithIssueTypes(this.selectedIssueReporterId).pipe(
            catchError(err => {
                console.warn('Failed to load Jira projects', err);
                return of([]);
            }),
            finalize(() => this.loadingJiraProjects = false),
            untilDestroyed(this)
        ).subscribe(projects => {
            this.jiraProjects = projects || [];
            this.jiraProjectOptions = this.jiraProjects.map(project => ({
                label: `${project.name} (${project.key})`,
                value: project.id
            }));
            if (this.jiraProjectOptions.length === 1) {
                this.onJiraProjectChange(this.jiraProjectOptions[0].value);
            }
        });
    }

    preloadDefaults(): void {
        if (!this.selectedIssueReporterId) {
            return;
        }

        this.loadingDefaults = true;
        this.testService.getIssueReporterDefaults(this.selectedIssueReporterId, this.data.projectId, this.data.issueSourceType, this.data.issueSourceId).pipe(
            catchError(err => {
                console.warn('Failed to preload defaults', err);
                return of<IssueReporterDefaults | undefined>(undefined);
            }),
            untilDestroyed(this)
        ).subscribe(defaults => {
            this.cachedDefaults = defaults;
            this.loadingDefaults = false;
            this.makeFormIfReady();
        });
    }

    loadReporterFields(): void {
        if (!this.selectedIssueReporterId || !this.selectedJiraProjectId || !this.selectedIssueTypeId) {
            return;
        }

        this.loadingFields = true;
        this.testService.getIssueReporterParameters(this.selectedIssueReporterId, this.selectedJiraProjectId, this.selectedIssueTypeId).pipe(
            catchError(err => {
                console.warn('Failed to load reporter fields', err);
                return of({ parameters: [], supportsAttachments: false });
            }),
            untilDestroyed(this)
        ).subscribe(response => {
            this.reporterFields = (response.parameters || []).map(field => ({
                ...field,
                optDisplayName: field.optDisplayName || field.name
            }));

            // Treat undefined or null as false
            this.supportsAttachments = response.supportsAttachments === true;

            // Filter and sort fields for form display
            const filtered = this.reporterFields.filter(field => {
                const name = field.name?.toLowerCase();
                return name !== 'project' && name !== 'issuetype';
            });

            const fieldPriority: Record<string, number> = {
                'summary': 1,
                'description': 2
            };

            this.formFields = filtered.sort((a, b) => {
                const aPriority = fieldPriority[a.name?.toLowerCase() || ''] || 999;
                const bPriority = fieldPriority[b.name?.toLowerCase() || ''] || 999;
                return aPriority - bPriority;
            });

            this.loadingFields = false;
            this.makeFormIfReady();
        });
    }

    makeFormIfReady(): void {
        if (this.loadingFields || this.loadingDefaults) {
            return;
        }

        // Only proceed if we have fields loaded
        if (this.reporterFields.length === 0) {
            return;
        }

        if (this.supportsAttachments && this.cachedDefaults?.availableAttachments) {
            this.setAttachments(this.cachedDefaults.availableAttachments);
        }

        const formControls = this.formFields.reduce((acc, field) => {
            const defaultValue = this.cachedDefaults?.parameterDefaults?.[field.name]?.data ?? null;
            acc[field.name] = [defaultValue, field.required ? Validators.required : []];
            return acc;
        }, {} as {[key: string]: any});

        this.form = this.formBuilder.group(formControls);

        // Reset submitted issue URL when any form field changes
        this.form.valueChanges.pipe(untilDestroyed(this)).subscribe(() => {
            if (this.submittedIssueUrl) {
                this.submittedIssueUrl = undefined;
            }
        });
    }

    setAttachments(attachments?: IssueReporterAttachment[]): void {
        this.attachments = (attachments || []).map((att, index) => ({
            ...att,
            key: this.buildAttachmentKey(att, index)
        }));
        this.selectedAttachments = {};
        this.attachments.forEach(att => {
            this.selectedAttachments[att.key] = true;
        });
    }

    attachmentPreviewUrl(attachment: IssueReporterAttachment): string | undefined {
        if (attachment.observation?.type === 'ScreenshotObservation' && attachment.observation.screenshotId) {
            return this.imageService.getImageUrl(attachment.observation.screenshotId, 'small');
        }
        return undefined;
    }

    onAttachmentSelectionChange(): void {
        if (this.submittedIssueUrl) {
            this.submittedIssueUrl = undefined;
        }
    }

    buildAttachmentKey(attachment: IssueReporterAttachment, index: number): string {
        const observation: any = attachment.observation || {};
        const type = observation.type || 'attachment';
        const parts = [type];
        if ('artifactId' in observation && observation.artifactId) {
            parts.push(`artifact:${observation.artifactId}`);
        }
        if ('screenshotId' in observation && observation.screenshotId) {
            parts.push(`screenshot:${observation.screenshotId}`);
        }
        if ('videoId' in observation && observation.videoId) {
            parts.push(`video:${observation.videoId}`);
        }
        if ('blobId' in observation && observation.blobId) {
            parts.push(`blob:${observation.blobId}`);
        }
        if (parts.length === 1) {
            parts.push(`idx:${index}`);
        }
        return parts.join('|');
    }

    attachmentTypeLabel(attachment: AttachmentWithKey): string {
        const type = attachment.observation?.type || 'Attachment';
        switch (type) {
            case 'ScreenshotObservation':
                return 'SCREENSHOT';
            case 'AttachmentObservation':
                return 'FILE';
            case 'VideoSnippetObservation':
                return 'VIDEO';
            case 'TestMailObservation':
                return 'MAIL';
            default:
                return type.replace(/Observation$/, '').toUpperCase();
        }
    }

    attachmentPreviewIcon(attachment: AttachmentWithKey): IconDefinition | null {
        if (this.attachmentPreviewUrl(attachment)) {
            return null;
        }

        switch (attachment.observation?.type) {
            case 'TestMailObservation':
                return this.faEnvelope;
            case 'AttachmentObservation':
                return this.faFile;
            case 'VideoSnippetObservation':
                return this.faVideo;
            case 'ScreenshotObservation':
                return this.faImage;
            default:
                return null;
        }
    }

    attachmentDisplayName(attachment: AttachmentWithKey): string {
        return attachment.displayName
            || (attachment.observation?.type === 'AttachmentObservation' ? attachment.observation.name : undefined)
            || attachment.observation?.type
            || 'Attachment';
    }

    isStringListField(field: IssueReporterParameter): boolean {
        return field.parameterType === 'StringListType';
    }

    isDropdownField(field: IssueReporterParameter): boolean {
        return !!field.possibleValues && field.parameterType !== 'StringListType';
    }

    isRichTextField(field: IssueReporterParameter): boolean {
        return !field.possibleValues && field.parameterType === 'RichText';
    }

    isNumberField(field: IssueReporterParameter): boolean {
        return !field.possibleValues && field.parameterType === 'Integer';
    }

    isDateField(field: IssueReporterParameter): boolean {
        return !field.possibleValues && field.parameterType === 'Date';
    }

    isDateTimeField(field: IssueReporterParameter): boolean {
        return !field.possibleValues && field.parameterType === 'DateTime';
    }

    isTextField(field: IssueReporterParameter): boolean {
        return field.parameterType === 'String';
    }

    findFieldByName(name: string): IssueReporterParameter | undefined {
        const lower = name.toLowerCase();
        return this.reporterFields.find(f => f.name?.toLowerCase() === lower);
    }

    getSelectedReporterBaseUri(): string | undefined {
        return this.issueReporters.find(r => r.id === this.selectedIssueReporterId)?.config?.['baseUri'];
    }

    fieldOptions(field: IssueReporterParameter): IssueFieldOption[] {
        return (field.possibleValues || []).map(([value, label]) => ({label, value}));
    }

    canContinueInJira(): boolean {
        return !!this.selectedIssueReporterId
            && !!this.selectedJiraProjectId
            && !!this.selectedIssueTypeId
            && !!this.getSelectedReporterBaseUri();
    }

    continueInJira(): void {
        if (!this.canContinueInJira()) {
            return;
        }

        const baseUri = this.getSelectedReporterBaseUri();

        const query = new URLSearchParams({
            pid: this.selectedJiraProjectId!,
            issuetype: this.selectedIssueTypeId!
        });

        const rawValues = this.form?.getRawValue?.() || {};
        Object.entries(rawValues).forEach(([key, value]) => {
            const lower = key.toLowerCase();
            // Skip fields already mapped explicitly
            if (['project', 'issuetype'].includes(lower)) {
                return;
            }
            const serializedValues = this.serializeFieldValueForUrl(key, value);
            serializedValues.forEach(v => query.append(key, v));
        });

        const url = `${baseUri.replace(/\/+$/, '')}/secure/CreateIssueDetails!init.jspa?${query.toString()}`;
        window.open(url, '_blank', 'noopener');
    }

    serializeFieldValueForUrl(fieldName: string, value: any): string[] {
        if (this.isEmptyFieldValue(value)) {
            return [];
        }

        const field = this.findFieldByName(fieldName);

        if (!field) return [];

        if (this.isDateField(field)) {
            const formatted = this.formatDateValue(value);
            return formatted ? [formatted] : [];
        }
        if (this.isDateTimeField(field)) {
            const formatted = this.formatDateTimeValue(value);
            return formatted ? [formatted] : [];
        }
        if (Array.isArray(value)) {
            // For StringListType, add each entry as its own query param
            if (field.parameterType === 'StringListType') {
                return value
                    .map(v => (v === null || v === undefined ? '' : `${v}`.trim()))
                    .filter(v => v !== '');
            }
            return value.map(v => (v === null || v === undefined ? '' : `${v}`)).filter(v => v !== '');
        }
        if (this.isRichTextField(field)) {
            return [this.toPlainText(`${value}`)];
        }
        if (typeof value === 'object') {
            return [JSON.stringify(value)];
        }
        return [`${value}`];
    }

    isEmptyFieldValue(value: any): boolean {
        if (value === null || value === undefined) {
            return true;
        }
        if (Array.isArray(value)) {
            return value.length === 0;
        }
        if (typeof value === 'string') {
            return value.trim() === '';
        }
        return false;
    }

    formatDateValue(value: any): string | undefined {
        const dateValue = this.toMomentDate(value);

        if (!dateValue?.isValid()) {
            return undefined;
        }

        return dateValue.format(IssueReporterComponent.JIRA_DATE_FORMAT);
    }

    formatDateTimeValue(value: any): string | undefined {
        const dateValue = this.toMomentDate(value);

        if (!dateValue?.isValid()) {
            return undefined;
        }

        return dateValue.toISOString();
    }

    toMomentDate(value: any): moment.Moment | undefined {
        if (value instanceof Date) {
            return moment(value);
        }
        if (value && typeof value.year === 'number' && typeof value.month === 'number' && typeof value.day === 'number') {
            return moment([value.year, value.month - 1, value.day]);
        }
        if (typeof value === 'string') {
            return moment(value);
        }
        if (moment.isMoment(value)) {
            return value;
        }
        return undefined;
    }

    getFieldValue(names: string[]): string {
        if (!this.form) {
            return '';
        }
        const raw = this.form.getRawValue?.() || {};
        const matchingKey = Object.keys(raw).find(k => names.some(name => name.toLowerCase() === k.toLowerCase()));
        if (!matchingKey) {
            return '';
        }
        const value = raw[matchingKey];
        return value ? `${value}` : '';
    }

    toPlainText(value: string): string {
        if (!value) {
            return '';
        }

        try {
            return htmlToText(value, {
                wordwrap: false,
                selectors: [{selector: 'a', format: 'anchor'}]
            }).trim();
        } catch (_err) {
            return String(value).trim();
        }
    }

    protected readonly faExternalLinkAlt = faExternalLinkAlt;
    protected readonly faEnvelope = faEnvelope;
    protected readonly faFile = faFile;
    protected readonly faImage = faImage;
    protected readonly faVideo = faVideo;
}
